import { useEffect, useMemo, useRef, useState } from 'react'
import Modal from './Modal.jsx'
import Icon from './Icon.jsx'
import { buildBands, UNGROUPED } from './FleetBand.jsx'
import { glyphFor } from '../lib/assets.js'
import { tintOf } from '../lib/theme.js'
import {
  PINNED_BAND,
  defaultAssetOrder,
  defaultPeopleOrder,
  groupSections,
  moveId,
  sameList,
  sortBands,
  sortByIds,
  toStored,
} from '../lib/boardOrder.js'

/**
 * Board view settings: the order this person reads the board in.
 *
 * Laid out as the board is — each group a band in its own colour, its people beneath — so
 * what is being arranged is recognisably the board and not a list about it. Everything moves
 * the same way: drag a row by its handle, or a group by its heading, and it goes where the
 * line says. The arrows are there for the keyboard and appear on hover or focus, so at rest
 * the dialog reads as the board, not as a grid of buttons.
 *
 * People move within their group, never between groups: that would be changing somebody's
 * group, which is the company's roster and is done on the board itself. "Not on a team" is
 * not a group and stays at the bottom — see lib/boardOrder.js — and so does the fleet's band
 * of machines in no group.
 *
 * Changes are a draft until Done, like every other settings dialog here. `board` is the board
 * as the server sent it, and live: somebody added while this is open shows up at the bottom
 * of their group, and somebody removed disappears, because every list is the draft applied
 * to what the board holds now rather than a copy taken when the dialog opened.
 */
export default function BoardOrderDialog({
  board,
  mode,
  groupBy,
  order,
  assetsName = 'Assets',
  unitName = 'asset',
  onSave,
  onClose,
}) {
  const hasFleet = Boolean(board.assets)
  const arrangeable = mode === 'cat'
  const [tab, setTab] = useState('people')
  // Which arrangement's people are being ordered. Opens on the one on screen; a Team board
  // has only the one, under its PMs.
  const [view, setView] = useState(arrangeable && groupBy === 'people' ? 'people' : 'group')

  // ── What the board holds now, in its own order: the defaults every list is kept against.
  const groupSecs = useMemo(() => groupSections(board), [board])
  const assetSecs = useMemo(
    () =>
      buildBands(board, unitName).map((b) => ({
        key: b.key,
        name: b.name,
        icon: b.icon,
        pinned: b.key === UNGROUPED,
        ids: b.assets.map((a) => a.id),
      })),
    [board, unitName],
  )
  const defaults = useMemo(
    () => ({
      bandOrder: groupSecs.filter((s) => !s.pinned).map((s) => s.key),
      people: defaultPeopleOrder(board),
      assets: defaultAssetOrder(board),
      assetBands: assetSecs.filter((s) => !s.pinned).map((s) => s.key),
    }),
    [board, groupSecs, assetSecs],
  )

  const saved = {
    bandOrder: order?.bands?.[mode] || null,
    bands: order?.groups?.[mode] || {},
    people: order?.people || null,
    assets: order?.assets || null,
    assetBands: order?.assetBands || null,
  }
  const [draft, setDraft] = useState(() => ({
    bandOrder: saved.bandOrder,
    bands: { ...saved.bands },
    people: saved.people,
    assets: saved.assets,
    assetBands: saved.assetBands,
  }))
  // Only lists somebody has changed are written — band by band for the people in groups. One
  // left alone keeps whatever it was, possibly nothing, which is what lets the board go on
  // following the company's order.
  const [touched, setTouched] = useState({ bands: {} })
  // Reset on the Groups view also clears lists kept for groups with nobody on this board
  // today; without it they would come back the next time those groups had anyone in them.
  const [groupsReset, setGroupsReset] = useState(false)

  // A flat list as it stands: the draft over whatever the board holds now.
  const now = (l) => sortByIds(defaults[l], draft[l])

  const list = tab === 'assets' ? 'assets' : view
  const sections = useMemo(() => {
    if (list === 'people') {
      return [{ key: 'everyone', name: 'Everyone', fixed: true, ids: sortByIds(defaults.people, draft.people) }]
    }
    if (list === 'group') {
      return sortBands(groupSecs, draft.bandOrder, (s) => s.key, PINNED_BAND).map((s) => ({
        ...s,
        ids: sortByIds(s.ids, draft.bands[s.key]),
      }))
    }
    return sortBands(assetSecs, draft.assetBands, (s) => s.key, UNGROUPED).map((s) => ({
      ...s,
      ids: sortByIds(s.ids, draft.assets),
    }))
  }, [list, groupSecs, assetSecs, defaults, draft])
  const bandOrderNow = sections.filter((s) => !s.pinned && !s.fixed).map((s) => s.key)
  const bandList = list === 'assets' ? 'assetBands' : 'bandOrder'

  const staffById = useMemo(() => new Map((board.staff || []).map((s) => [s.id, s])), [board])
  const assetById = useMemo(() => new Map((board.assets || []).map((a) => [a.id, a])), [board])
  const nameOf = (id) => (list === 'assets' ? assetById.get(id)?.name : staffById.get(id)?.name) || 'Unknown'

  // ── Moving things ──────────────────────────────────────────────────────────────────────
  const moveItem = (section, id, target, after) => {
    if (list === 'group') {
      const band = groupSecs.find((b) => b.key === section.key)
      if (!band) return
      setDraft((d) => ({
        ...d,
        bands: { ...d.bands, [band.key]: moveId(sortByIds(band.ids, d.bands[band.key]), id, target, after) },
      }))
      setTouched((t) => ({ ...t, bands: { ...t.bands, [band.key]: true } }))
      return
    }
    setDraft((d) => ({ ...d, [list]: moveId(sortByIds(defaults[list], d[list]), id, target, after) }))
    setTouched((t) => ({ ...t, [list]: true }))
  }
  const moveBand = (key, target, after) => {
    setDraft((d) => ({ ...d, [bandList]: moveId(bandOrderNow, key, target, after) }))
    setTouched((t) => ({ ...t, [bandList]: true }))
  }

  // Keyboard: whatever moved is a DOM node moved, and the focused arrow goes with it only in
  // some browsers. Put focus back on the arrow that was pressed, or on its twin once the thing
  // reaches the end and that arrow disables itself. Keys are ids — no quote can appear in
  // one — so they go into the selector as they are.
  const bodyRef = useRef(null)
  const [refocus, setRefocus] = useState(null) // { selector, dir }
  useEffect(() => {
    if (!refocus || !bodyRef.current) return
    const host = bodyRef.current.querySelector(refocus.selector)
    const pressed = host?.querySelector(`:scope > .order-arrows button[data-dir="${refocus.dir}"]`)
    const other = host?.querySelector(`:scope > .order-arrows button[data-dir="${refocus.dir === 'up' ? 'down' : 'up'}"]`)
    ;(pressed && !pressed.disabled ? pressed : other)?.focus()
    setRefocus(null)
  }, [refocus])

  const stepItem = (section, i, dir) => {
    const neighbour = section.ids[i + (dir === 'up' ? -1 : 1)]
    if (!neighbour) return
    moveItem(section, section.ids[i], neighbour, dir === 'down')
    setRefocus({ selector: `[data-row-key="${section.key}:${section.ids[i]}"]`, dir })
  }
  const stepBand = (key, dir) => {
    const i = bandOrderNow.indexOf(key)
    const neighbour = bandOrderNow[i + (dir === 'up' ? -1 : 1)]
    if (!neighbour) return
    moveBand(key, neighbour, dir === 'down')
    setRefocus({ selector: `[data-band-key="${key}"] > .order-card-head`, dir })
  }

  // Dragging. A row moves within its own band and a band among the others; a drop anywhere
  // else is refused rather than guessed at. Dropped on something, the dragged thing takes its
  // place — after it when coming from above, before it when coming from below — and the line
  // is drawn on the side it will land.
  const [drag, setDrag] = useState(null) // { kind: 'item' | 'band', section, id }
  const [over, setOver] = useState(null) // { key, after }
  // While a band is being dragged the others fold down to their headings, so moving the last
  // group to the top is a short drag rather than a scroll. Deferred a tick: changing the
  // layout inside dragstart itself makes Chrome abandon the drag.
  const [compact, setCompact] = useState(false)
  const endDrag = () => {
    setDrag(null)
    setOver(null)
    setCompact(false)
  }
  const itemAfter = (section, targetId) => section.ids.indexOf(drag.id) < section.ids.indexOf(targetId)
  const bandAfter = (targetKey) => bandOrderNow.indexOf(drag.id) < bandOrderNow.indexOf(targetKey)

  const startDrag = (e, next) => {
    e.stopPropagation()
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', next.id)
    setDrag(next)
    if (next.kind === 'band') setTimeout(() => setCompact(true), 0)
  }
  const overItem = (e, section, id) => {
    if (drag?.kind !== 'item' || drag.section !== section.key) return
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
    const key = `${section.key}:${id}`
    if (drag.id === id) {
      if (over) setOver(null)
      return
    }
    const after = itemAfter(section, id)
    if (over?.key !== key || over.after !== after) setOver({ key, after })
  }
  const overBand = (e, section) => {
    if (drag?.kind !== 'band' || section.pinned || section.fixed) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (drag.id === section.key) {
      if (over) setOver(null)
      return
    }
    const after = bandAfter(section.key)
    if (over?.key !== section.key || over.after !== after) setOver({ key: section.key, after })
  }
  const dropItem = (e, section, id) => {
    if (drag?.kind !== 'item') return
    e.preventDefault()
    e.stopPropagation()
    if (drag.section === section.key && drag.id !== id) moveItem(section, drag.id, id, itemAfter(section, id))
    endDrag()
  }
  const dropBand = (e, section) => {
    if (drag?.kind !== 'band') return
    e.preventDefault()
    if (!section.pinned && !section.fixed && drag.id !== section.key) moveBand(drag.id, section.key, bandAfter(section.key))
    endDrag()
  }

  // ── Reset and save ─────────────────────────────────────────────────────────────────────
  const groupBandOrder = () => sortByIds(defaults.bandOrder, draft.bandOrder)
  const assetBandOrder = () => sortByIds(defaults.assetBands, draft.assetBands)
  const listsOffBoard = Object.keys(draft.bands).some((key) => !groupSecs.some((s) => s.key === key))
  const isDefault = (l) => {
    if (l === 'group') {
      return (
        sameList(groupBandOrder(), defaults.bandOrder) &&
        groupSecs.every((s) => sameList(sortByIds(s.ids, draft.bands[s.key]), s.ids)) &&
        !listsOffBoard
      )
    }
    if (l === 'assets') return sameList(now('assets'), defaults.assets) && sameList(assetBandOrder(), defaults.assetBands)
    return sameList(now('people'), defaults.people)
  }
  const reset = () => {
    if (list === 'group') {
      setDraft((d) => ({ ...d, bandOrder: null, bands: {} }))
      setTouched((t) => ({ ...t, bandOrder: true, bands: {} }))
      setGroupsReset(true)
    } else if (list === 'assets') {
      setDraft((d) => ({ ...d, assets: null, assetBands: null }))
      setTouched((t) => ({ ...t, assets: true, assetBands: true }))
    } else {
      setDraft((d) => ({ ...d, people: null }))
      setTouched((t) => ({ ...t, people: true }))
    }
  }

  const save = (e) => {
    e.preventDefault()
    const keep = (l, current, previous = saved[l]) => (touched[l] ? toStored(current, defaults[l], previous) : saved[l])
    // Band by band: a band nobody touched keeps its list exactly, and one put back in its own
    // order is dropped. Bands not on this board today keep theirs, unless reset.
    const bands = groupsReset ? {} : { ...saved.bands }
    for (const s of groupSecs) {
      if (!touched.bands[s.key]) continue
      const kept = toStored(sortByIds(s.ids, draft.bands[s.key]), s.ids, groupsReset ? null : saved.bands[s.key])
      if (kept) bands[s.key] = kept
      else delete bands[s.key]
    }
    onSave({
      bands: { ...(order?.bands || {}), [mode]: keep('bandOrder', groupBandOrder(), groupsReset ? null : saved.bandOrder) },
      groups: { ...(order?.groups || {}), [mode]: Object.keys(bands).length ? bands : null },
      people: keep('people', now('people')),
      assets: keep('assets', now('assets')),
      assetBands: keep('assetBands', assetBandOrder()),
    })
  }

  const nothing = sections.every((s) => s.ids.length === 0)
  const empty = list === 'assets' ? `No ${unitName}s on this board.` : 'Nobody on this board.'
  const caption =
    list === 'assets'
      ? 'The same order whichever way the people are arranged.'
      : list === 'people'
        ? 'Everybody as one list.'
        : mode === 'cat'
          ? 'People move within their group. Not on a team stays at the bottom.'
          : 'People move within their PM’s list. Unassigned staff stay at the bottom.'

  return (
    <Modal title="Board view settings" onClose={onClose} width={500} className="order-modal">
      <form onSubmit={save} className="order-dialog">
        {/* Two views, named for what is in them. The arrangement below is a setting of the
            Staff view rather than a second row of tabs, so it is smaller and sits with the
            list it changes; "People" appearing in both rows read as four tabs, two of them
            the same. The fleet's view is there for every company, greyed out where the board
            draws no fleet. */}
        <div className="order-tabs">
          <Segmented
            label="View"
            value={tab}
            onChange={setTab}
            options={[
              { value: 'people', label: 'Staff view' },
              {
                value: 'assets',
                label: `${assetsName} view`,
                disabled: !hasFleet,
                title: hasFleet ? undefined : `Your company does not show ${assetsName.toLowerCase()} on the board`,
              },
            ]}
          />
        </div>

        {tab === 'people' && arrangeable && (
          <div className="order-view">
            <span className="order-view-label">Categorized by</span>
            <Segmented
              small
              label="Categorized by"
              value={view}
              onChange={setView}
              options={[
                { value: 'group', label: 'Groups' },
                { value: 'people', label: 'People' },
              ]}
            />
          </div>
        )}

        <div className={`order-canvas ${compact ? 'compact' : ''}`} ref={bodyRef}>
          {nothing && <div className="order-empty">{empty}</div>}
          {!nothing &&
            sections.map((section) => {
              const movable = !section.pinned && !section.fixed
              const bi = bandOrderNow.indexOf(section.key)
              const headTint = section.color ? tintOf(section.color, 0.8) : null
              const rowTint = section.color ? tintOf(section.color, 0.94) : null
              const Glyph = list === 'assets' && section.icon ? glyphFor(section.icon) : null
              return (
                <section
                  key={section.key}
                  data-band-key={section.key}
                  role="group"
                  aria-label={section.name}
                  className={[
                    'order-card',
                    list === 'assets' ? 'fleet' : '',
                    drag?.kind === 'band' && drag.id === section.key ? 'dragging' : '',
                    drag?.kind === 'band' && over?.key === section.key ? (over.after ? 'drop-after' : 'drop-before') : '',
                  ].join(' ')}
                  onDragOver={(e) => overBand(e, section)}
                  onDrop={(e) => dropBand(e, section)}
                >
                  <div
                    className={`order-card-head ${movable ? 'movable' : ''}`}
                    style={headTint ? { '--team-tint': headTint } : undefined}
                    draggable={movable}
                    onDragStart={movable ? (e) => startDrag(e, { kind: 'band', section: null, id: section.key }) : undefined}
                    onDragEnd={endDrag}
                    title={section.pinned ? 'Always at the bottom' : undefined}
                  >
                    {Glyph ? (
                      <span className="fleet-band-icon"><Glyph size={15} strokeWidth={1.9} /></span>
                    ) : section.color ? (
                      <span className="group-swatch" style={{ background: section.color }} />
                    ) : null}
                    <span className="order-card-name">
                      {section.name}
                      {section.subtitle && <span className="group-sub">{section.subtitle}</span>}
                    </span>
                    <span className="group-count">{section.ids.length}</span>
                    {movable && (
                      <Arrows
                        name={section.name}
                        first={bi === 0}
                        last={bi === bandOrderNow.length - 1}
                        onStep={(dir) => stepBand(section.key, dir)}
                      />
                    )}
                    {movable ? (
                      <span className="order-handle" aria-hidden="true"><Icon name="reorder" size={16} /></span>
                    ) : (
                      <span className="order-handle-space" />
                    )}
                  </div>

                  <div className="order-rows">
                    {section.ids.map((id, i) => {
                      const rowKey = `${section.key}:${id}`
                      const name = nameOf(id)
                      return (
                        <div
                          key={id}
                          data-row-key={rowKey}
                          className={[
                            'order-row',
                            drag?.kind === 'item' && drag.section === section.key && drag.id === id ? 'dragging' : '',
                            drag?.kind === 'item' && over?.key === rowKey ? (over.after ? 'drop-after' : 'drop-before') : '',
                          ].join(' ')}
                          style={rowTint ? { '--team-tint': rowTint } : undefined}
                          draggable
                          onDragStart={(e) => startDrag(e, { kind: 'item', section: section.key, id })}
                          onDragOver={(e) => overItem(e, section, id)}
                          onDragLeave={() => setOver((o) => (o?.key === rowKey ? null : o))}
                          onDrop={(e) => dropItem(e, section, id)}
                          onDragEnd={endDrag}
                        >
                          <span className="order-name">{name}</span>
                          <Arrows
                            name={name}
                            first={i === 0}
                            last={i === section.ids.length - 1}
                            onStep={(dir) => stepItem(section, i, dir)}
                          />
                          <span className="order-handle" aria-hidden="true"><Icon name="reorder" size={16} /></span>
                        </div>
                      )
                    })}
                  </div>
                </section>
              )
            })}
        </div>

        <p className="order-caption">
          {caption} Drag to rearrange. Only you see this, until you close the tab.
        </p>

        <div className="modal-actions">
          <button
            type="button"
            className="btn ghost"
            onClick={reset}
            disabled={isDefault(list)}
            title={isDefault(list) ? 'Already in the default order' : 'Put this back in the default order'}
          >
            Reset
          </button>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary">Done</button>
        </div>
      </form>
    </Modal>
  )
}

/** Up and down, for the keyboard. Shown on hover and on focus; always there to tab to. */
function Arrows({ name, first, last, onStep }) {
  return (
    <span className="order-arrows">
      <button
        type="button"
        className="icon-btn"
        data-dir="up"
        title="Move up"
        aria-label={`Move ${name} up`}
        disabled={first}
        onClick={() => onStep('up')}
      >
        <Icon name="up" size={15} />
      </button>
      <button
        type="button"
        className="icon-btn"
        data-dir="down"
        title="Move down"
        aria-label={`Move ${name} down`}
        disabled={last}
        onClick={() => onStep('down')}
      >
        <Icon name="down" size={15} />
      </button>
    </span>
  )
}

function Segmented({ label, value, onChange, options, small = false }) {
  return (
    <div className={`segmented ${small ? 'sm' : ''}`} role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={value === o.value ? 'selected' : ''}
          aria-pressed={value === o.value}
          disabled={o.disabled}
          title={o.title}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
